import { useCallback, useEffect, useState } from 'react';

import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * The async storage value will be `undefined` while it's loading.
 * Once it has been loaded, it will contain an empty string if it was missing.
 * Otherwise it will contain the stored string value.
 *
 * This is so that its evaluation can be delayed by checking for `undefined`.
 */
export const useAsyncStorage = (key: string): AsyncStorageHook => {
  const [value, setValue] = useState<string | undefined>(undefined);

  const refetch = useCallback(async () => {
    const persisted = await AsyncStorage.getItem(key);
    setValue(persisted ?? '');
    return persisted;
  }, [key]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const update = useCallback(
    (payload: string) => {
      setValue(payload);
      void AsyncStorage.setItem(key, payload);
    },
    [key],
  );

  return [value, update, refetch];
};

type AsyncStorageHook = readonly [
  string | undefined,
  (value: string) => void,
  () => Promise<string | null>,
];
